<script setup>
import {
  computed, inject, ref, watch,
} from 'vue';
import { Dialog, DialogPanel } from '@headlessui/vue';
import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import Trans from '@/i18n/translation';
import GamebookLogo from '@/components/GamebookLogo.vue';
import { useGlobalState } from '@/store/user.store';
import LogoutIcon from '@/components/CustomIcons/LogoutIcon.vue';
import { trackEvent } from '@/services/analytics/googleAnalytics.js';

const i18n = useI18n();
const homeNavigation = Trans.i18nRoute({ name: 'home' });
const loginNavigation = Trans.i18nRoute({ name: 'login' });
const dashboardNavigation = Trans.i18nRoute({ name: 'dashboard' });
const getStartedNavigation = Trans.i18nRoute({ name: 'register' });
const activeSection = ref(inject('activeSection'));
const navigationData = [
  {
    name: i18n.t('nav.whyCyberalarm'),
    href: Trans.i18nRoute({ name: 'home', hash: '#why-cyberalarm' }),
  },
  {
    name: i18n.t('nav.howItWorks'),
    href: Trans.i18nRoute({ name: 'home', hash: '#how-it-works' }),
  },
  { name: i18n.t('nav.courses'), href: Trans.i18nRoute({ name: 'home', hash: '#courses' }) },
  { name: i18n.t('nav.pricing'), href: Trans.i18nRoute({ name: 'home', hash: '#pricing' }) },
  { name: i18n.t('nav.alarm'), href: Trans.i18nRoute({ name: 'home', hash: '#research' }) },
  { name: i18n.t('nav.aboutUs'), href: Trans.i18nRoute({ name: 'home', hash: '#about-us' }) },
];
const extractHash = (href) => {
  if (typeof href === 'object' && href.hash) {
    return href.hash.replace('#', '');
  }
  const hrefString = typeof href === 'string' ? href : href?.toString?.() || '';
  const index = hrefString.indexOf('#');
  return index !== -1 ? hrefString.slice(index + 1) : '';
};

let sectionTimer = null;
const lastTrackedSection = ref(null);

const isActive = (href) => {
  const targetHash = extractHash(href);
  return activeSection.value === targetHash;
};

watch(
  activeSection,
  (newVal) => {
    if (sectionTimer) {
      clearTimeout(sectionTimer);
    }

    if (newVal && newVal !== lastTrackedSection.value) {
      sectionTimer = setTimeout(() => {
        const eventName = `viewed_${newVal}`;
        trackEvent(eventName, {
          event_category: 'LandingPage',
          event_label: newVal,
        });
        lastTrackedSection.value = newVal;
      }, 2000);
    }
  },
);

const navLinkClasses = computed(() => (href) => {
  const baseClasses = 'text-sm leading-6 transition-colors duration-300 ease-in-out';
  const isActiveLink = isActive(href);
  return `${baseClasses} ${isActiveLink ? 'text-secondary' : 'text-primary hover:opacity-50'}`;
});
const { isAuthenticated } = useGlobalState();

const mobileMenuOpen = ref(false);

</script>

<template>
  <header class="fixed z-50 top-0 start-0 w-full bg-basecolor text-primary after:absolute after:bottom-0 after:w-full after:h-[3px] after:bg-highlightLight">
    <nav
      class="relative mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-1"
    >
      <div class="flex items-center gap-x-12">
        <RouterLink
          :to="homeNavigation"
          class="-m-1.5 p-1.5 inline-flex"
        >
          <span class="sr-only">{{ $t('appTitle') }}</span>
          <GamebookLogo class="flex-col h-12 w-auto" />
          <span class="font-bold p-3 flex-col">{{ $t('appTitle') }}</span>
        </RouterLink>
        <div class="relative hidden lg:flex lg:gap-x-12">
          <div class="hidden lg:flex lg:gap-x-12">
            <RouterLink
              v-for="item in navigationData"
              :key="item.name"
              :to="item.href"
              class="relative pb-2"
            >
              <span :class="navLinkClasses(item.href)">
                {{ item.name }}
              </span>
              <span
                class="absolute left-0 top-[3.5rem] z-50 origin-left h-[7px] rounded-t bg-secondary transition-all duration-300 ease-in-out"
                :class="{
                  'w-full opacity-100': isActive(item.href),
                  'w-0 group-hover:w-full group-hover:opacity-50': !isActive(item.href),
                }"
              />
            </RouterLink>
          </div>
        </div>
      </div>
      <div class="flex lg:hidden">
        <button
          type="button"
          class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5"
          @click="mobileMenuOpen = true"
        >
          <span class="sr-only">Open main menu</span>
          <Bars3Icon
            class="h-6 w-6"
            aria-hidden="true"
          />
        </button>
      </div>
      <div class="hidden lg:flex items-center gap-x-4">
        <template v-if="isAuthenticated()">
          <RouterLink
            :to="dashboardNavigation"
            class="btn-transparent text-sm px-3 xl:px-4 py-1.5"
          >
            {{ $t('dashboardBtn') }}
          </RouterLink>
          <RouterLink
            to="/logout"
            class="btn-transparent text-sm px-3 xl:px-4 py-1.5"
          >
            <LogoutIcon class="float-left mr-5" />
            {{ $t('logOut') }}
          </RouterLink>
        </template>

        <template v-else>
          <RouterLink
            :to="loginNavigation"
            class="btn-transparent text-sm px-6 xl:px-12 py-1.5"
          >
            {{ $t('logIn') }}
          </RouterLink>

          <RouterLink
            :to="getStartedNavigation"
            class="btn-cta text-sm px-4 xl:px-8 py-1.5"
          >
            {{ $t('getStartedCTA') }}
          </RouterLink>
        </template>
      </div>
    </nav>
    <Dialog
      class="lg:hidden"
      @close="mobileMenuOpen = false"
      :open="mobileMenuOpen"
    >
      <div class="fixed inset-0 z-50" />
      <DialogPanel
        class="text-primary fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-basecolor px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10"
      >
        <div class="flex items-center justify-between">
          <RouterLink
            @click="mobileMenuOpen = false"
            :to="homeNavigation"
            class="-m-1.5 p-1.5"
          >
            <span class="sr-only">{{ $t('appTitle') }}</span>
            <GamebookLogo class="h-8 w-auto rounded-md" />
          </RouterLink>
          <button
            type="button"
            class="-m-2.5 rounded-md p-2.5"
            @click="mobileMenuOpen = false"
          >
            <span class="sr-only">{{ $t('nav.closeMenu') }}</span>
            <XMarkIcon
              class="h-6 w-6"
              aria-hidden="true"
            />
          </button>
        </div>
        <div class="mt-6 flow-root">
          <div class="-my-6 divide-y divide-gray-500/10">
            <div class="space-y-2 py-6">
              <RouterLink
                @click="mobileMenuOpen = false"
                v-for="item in navigationData"
                :key="item.name"
                :to="item.href"
                class="text-sm -mx-3 block rounded-lg px-3 py-2 leading-7 hover:bg-gray-50 hover:text-basecolor"
                :class="{
                  'bg-secondary': isActive(item.href),
                }"
              >
                {{ item.name }}
              </RouterLink>
            </div>
            <div class="py-6">
              <template v-if="isAuthenticated()">
                <RouterLink
                  @click="mobileMenuOpen = false"
                  :to="dashboardNavigation"
                  class="-mx-3 block rounded-lg px-3 py-2.5 font-semibold leading-7 hover:bg-gray-50 hover:text-basecolor"
                >
                  {{ $t('dashboardBtn') }}
                </RouterLink>
                <RouterLink
                  @click="mobileMenuOpen = false"
                  to="/logout"
                  class="-mx-3 block rounded-lg px-3 py-2.5 font-semibold leading-7 hover:bg-gray-50 hover:text-basecolor flex items-center"
                >
                  <LogoutIcon class="mr-2 h-5 w-5" />
                  {{ $t('logOut') }}
                </RouterLink>
              </template>
              <template v-else>
                <RouterLink
                  @click="mobileMenuOpen = false"
                  :to="loginNavigation"
                  class="-mx-3 block rounded-lg px-3 py-2.5 font-semibold leading-7 hover:bg-gray-50 hover:text-basecolor"
                >
                  {{ $t('logIn') }}
                </RouterLink>
                <RouterLink
                  @click="mobileMenuOpen = false"
                  :to="getStartedNavigation"
                  class="-mx-3 block rounded-lg px-3 py-2.5 font-semibold leading-7 hover:bg-gray-50 hover:text-basecolor"
                >
                  {{ $t('getStartedCTA') }}
                </RouterLink>
              </template>
            </div>
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  </header>
</template>
